
import { FastingProtocol } from "@/types/fasting";
import { FastingPersistentState } from './types';
import { 
  saveDataToCookies, 
  loadDataFromCookies, 
  clearSavedData,
  hasCookieConsent 
} from "@/utils/cookies";

/**
 * Loads fasting state from cookies using our cookie utility
 * @returns Partial fasting state from cookies or null if not found
 */
export const loadFastingStateFromCookies = (protocols: FastingProtocol[]): FastingPersistentState | null => {
  try {
    // Only try to load cookies if consent was given
    if (!hasCookieConsent()) {
      console.log('Skipping cookie load - no consent given');
      return null;
    }
    
    const cookieState = loadDataFromCookies(protocols);
    
    // Return null if cookies aren't available or no data was found
    if (!cookieState) {
      return null;
    }
    
    // Return cookie data even if isActive is false, as long as we have cookie data
    // This ensures we can still restore the timer state when it's paused or stopped
    if (cookieState && typeof cookieState.isActive === 'boolean') {
      console.info("Successfully loaded fasting state from cookies", {
        isActive: cookieState.isActive,
        protocol: cookieState.selectedProtocol?.name,
        startTime: cookieState.startTime ? new Date(cookieState.startTime).toLocaleString() : 'none',
        timeElapsed: cookieState.timeElapsed
      });
      
      return {
        isActive: cookieState.isActive,
        isPaused: cookieState.isPaused || false,
        timeElapsed: cookieState.timeElapsed || 0,
        selectedProtocolId: cookieState.selectedProtocol?.id,
        selectedProtocol: cookieState.selectedProtocol,
        waterIntake: cookieState.waterIntake || 0,
        startTime: cookieState.startTime,
        pausedTime: cookieState.pausedTime || null,
        endTime: cookieState.endTime || null,
        totalPauseDuration: cookieState.totalPauseDuration || 0
      };
    }
    
    console.log("No valid state found in cookies or isActive is undefined");
    return null;
  } catch (error) {
    console.error("Error loading fasting state from cookies:", error);
    return null;
  }
};

/**
 * Saves state to cookies if consent is given
 */
export const saveToCookies = (state: {
  isActive: boolean;
  isPaused: boolean;
  timeElapsed: number;
  selectedProtocol: FastingProtocol;
  waterIntake: number;
  startTime: number | null;
  pausedTime: number | null;
  endTime: number | null;
  totalPauseDuration: number;
}): void => {
  if (hasCookieConsent()) {
    saveDataToCookies({
      isActive: state.isActive,
      isPaused: state.isPaused,
      timeElapsed: state.timeElapsed,
      selectedProtocol: state.selectedProtocol,
      waterIntake: state.waterIntake,
      startTime: state.startTime,
      pausedTime: state.pausedTime,
      endTime: state.endTime,
      totalPauseDuration: state.totalPauseDuration
    });
  } else {
    console.log('Skipping cookie save - no consent given');
  }
};

/**
 * Clears cookie data if consent was given
 */
export const clearCookies = (): void => {
  if (hasCookieConsent()) {
    clearSavedData();
  }
};
